/**
 * Normas escalares para pruebas con un único puntaje (EEL_12, RA_10, PCL_10).
 * Jerarquía: company → industry → country → global.
 * Se usa scorePercent (0-100) como valor agregado.
 */

import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  type Firestore,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export const SCALAR_NORM_TESTS = ['EEL_12', 'RA_10', 'PCL_10'] as const;
export type ScalarNormTestId = (typeof SCALAR_NORM_TESTS)[number];

const NORM_VERSION: Record<ScalarNormTestId, string> = {
  EEL_12: 'eel_12_v1',
  RA_10: 'ra_10_v1',
  PCL_10: 'pcl_10_v1',
};

export interface ScalarNormDoc {
  normVersion: string;
  scopeType: 'company' | 'industry' | 'country' | 'global';
  scopeKey: string;
  n: number;
  sum: number;
  sumSq: number;
  mean: number;
  sd: number;
  updatedAt: ReturnType<typeof serverTimestamp>;
}

function scopeId(scopeType: string, scopeKey: string): string {
  if (scopeType === 'global') return 'global';
  if (scopeType === 'country') return `country:${scopeKey}`;
  return `${scopeType}:${scopeKey}`;
}

function computeSd(sum: number, sumSq: number, n: number): number {
  if (n <= 0) return 0;
  const mean = sum / n;
  const variance = Math.max(sumSq / n - mean * mean, 1e-6);
  return Math.sqrt(variance);
}

export interface UpdateScalarNormParams {
  testId: ScalarNormTestId;
  companyId: string;
  industryKey?: string | null;
  countryCode?: string | null;
  excludeFromNorms: boolean;
  scorePercent: number;
  firestore?: Firestore;
}

/**
 * Actualiza las normas con un nuevo resultado.
 * excludeFromNorms=true → no actualiza nada
 * excludeFromNorms=false → company, industry, country (si aplica), global
 */
export async function updateScalarNorm(params: UpdateScalarNormParams): Promise<void> {
  const firestore = params.firestore ?? db;
  if (params.excludeFromNorms) return;

  const { testId, companyId, scorePercent } = params;
  const version = NORM_VERSION[testId];

  const scopes: { scopeType: 'company' | 'industry' | 'country' | 'global'; scopeKey: string }[] = [
    { scopeType: 'company', scopeKey: companyId },
    { scopeType: 'industry', scopeKey: params.industryKey ?? '' },
    { scopeType: 'country', scopeKey: params.countryCode ?? '' },
    { scopeType: 'global', scopeKey: 'global' },
  ].filter((s) => {
    if (s.scopeType === 'industry') return !!s.scopeKey;
    if (s.scopeType === 'country') return !!s.scopeKey;
    return true;
  });

  for (const { scopeType, scopeKey } of scopes) {
    const id = scopeId(scopeType, scopeKey);
    const ref = doc(firestore, 'norms', testId, 'scopes', id);

    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(ref);
      let n = 0;
      let sum = 0;
      let sumSq = 0;

      if (snap.exists()) {
        const data = snap.data() as ScalarNormDoc;
        if (data.normVersion !== version) return;
        n = data.n;
        sum = data.sum;
        sumSq = data.sumSq;
      }

      n += 1;
      sum += scorePercent;
      sumSq += scorePercent * scorePercent;
      const mean = sum / n;
      const sd = computeSd(sum, sumSq, n);

      tx.set(ref, {
        normVersion: version,
        scopeType,
        scopeKey,
        n,
        sum,
        sumSq,
        mean,
        sd,
        updatedAt: serverTimestamp(),
      });
    });
  }
}

// ==========================================
// LÓGICA DE LECTURA Y APLICACIÓN DE NORMAS
// ==========================================
export const MIN_N_VALID = 30;
export const MIN_SD_VALID = 0;

export interface ScalarNormResult {
  scopeId: string;
  scopeType: 'company' | 'industry' | 'country' | 'global';
  n: number;
  mean: number;
  sd: number;
}

export function isScalarNormValid(n: number, sd: number): boolean {
  return n >= MIN_N_VALID && sd > MIN_SD_VALID;
}

export function computeZScore(score: number, mean: number, sd: number): number | null {
  if (sd <= 0) return null;
  return (score - mean) / sd;
}

export interface GetScalarNormParams {
  testId: 'EEL_12' | 'RA_10' | 'PCL_10';
  companyId: string;
  industryKey?: string | null;
  countryCode?: string | null;
  firestore?: any;
}

export async function getScalarNorm(params: GetScalarNormParams): Promise<ScalarNormResult | null> {
  const firestoreInstance = params.firestore ?? db;
  
  const scopes = [
    { type: 'company', key: params.companyId },
    { type: 'industry', key: params.industryKey },
    { type: 'country', key: params.countryCode },
    { type: 'global', key: 'global' },
  ];

  for (const scope of scopes) {
    if (!scope.key) continue; 
    
    // Asume que la función scopeId ya existe arriba en este mismo archivo
    const currentScopeId = scope.type === 'global' ? 'global' : 
                           scope.type === 'country' ? `country:${scope.key}` : 
                           `${scope.type}:${scope.key}`;
                           
    const ref = doc(firestoreInstance, 'norms', params.testId, 'scopes', currentScopeId);
    
    try {
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() as any;
        if (isScalarNormValid(data.n, data.sd)) {
          return { scopeId: currentScopeId, scopeType: data.scopeType, n: data.n, mean: data.mean, sd: data.sd };
        }
      }
    } catch (error) {
      console.error(`[Normas] Error leyendo norma para ${currentScopeId}:`, error);
      continue; 
    }
  }
  return null;
}
